import {QTreeNode} from "quasar";
import {CustomDTOExpression} from "@/utils/presenters/interfaces.ts";
import Operator from "@/utils/constants/Operator.ts";
import ExpressionRelationship from "@/utils/constants/ExpressionRelationship.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import ListViewUtils from "@/utils/common/ListViewUtils.ts";
import ExpressionEditUtility from "@/utils/common/ExpressionEditUtility.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import DTOCriteria = namaTypes.DTOCriteria;
import CriteriaField = namaTypes.CriteriaField;

export default class CriteriaTreeUtils
{

  static flattenTree(tree?: CustomDTOExpression[]): CustomDTOExpression[] {
    if (!tree || !tree.length)
      return [];
    return tree.flatMap(e => this.flatten(e));
  }

  private static flatten(e: CustomDTOExpression, parent?: CustomDTOExpression): CustomDTOExpression[] {
    if (!e)
      return [];
    if (e.operator !== Operator.OpenBracket)
      return [e];
    if (!e.children || !e.children.length)
      return [];
    let subExpressions = e.children.flatMap(ch => this.flatten(ch, e));
    if (!subExpressions || !subExpressions.length)
      return [];
    subExpressions.unshift(e);
    let close: CustomDTOExpression = {operator: Operator.CloseBracket, relation: parent ? parent.relation : e.relation};
    subExpressions.push(close);
    return subExpressions;
  }

  static normalizeCachedCriteria(criteria: DTOCriteria | undefined): DTOCriteria | undefined {
    if (!criteria || !criteria.expressions || !criteria.expressions.length)
      return criteria;
    let expressions = criteria.expressions as CustomDTOExpression[];
    if (this.isTreeForm(expressions))
      expressions = this.flattenTree(expressions).map(e => ({...e, children: undefined, id: undefined}));
    expressions = this.stripRedundantOuterBracket(expressions);
    return {...criteria, fromVue: false, expressions};
  }

  private static isTreeForm(expressions: CustomDTOExpression[]): boolean {
    return expressions.some(e => !!e.children && e.children.length > 0);
  }

  private static stripRedundantOuterBracket(expressions: CustomDTOExpression[]): CustomDTOExpression[] {
    if (expressions.length < 2 || expressions[0].operator !== Operator.OpenBracket)
      return expressions;
    let depth = 0;
    let matchingCloseIndex = -1;
    for (let i = 0; i < expressions.length; i++) {
      if (expressions[i].operator === Operator.OpenBracket)
        depth++;
      else if (expressions[i].operator === Operator.CloseBracket) {
        depth--;
        if (depth === 0) {
          matchingCloseIndex = i;
          break;
        }
      }
    }
    if (matchingCloseIndex !== expressions.length - 1)
      return expressions;
    let inner = expressions.slice(1, expressions.length - 1);
    if (this.hasTopLevelOr(inner))
      return this.andOuterBracketWithWhatFollows(expressions);
    return inner;
  }

  private static andOuterBracketWithWhatFollows(bracketed: CustomDTOExpression[]): CustomDTOExpression[] {
    let closeBracket = {...bracketed[bracketed.length - 1], relation: ExpressionRelationship.AND};
    return [...bracketed.slice(0, -1), closeBracket];
  }

  private static hasTopLevelOr(expressions: CustomDTOExpression[]): boolean {
    let depth = 0;
    for (let e of expressions) {
      if (e.operator === Operator.OpenBracket)
        depth++;
      else if (e.operator === Operator.CloseBracket)
        depth--;
      if (depth === 0 && e.relation === ExpressionRelationship.OR)
        return true;
    }
    return false;
  }

  static criteriaToTree(criteria: DTOCriteria | undefined, criteriaFields?: CriteriaField[]): QTreeNode<CustomDTOExpression>[] {
    const roots: QTreeNode<CustomDTOExpression>[] = [];
    let mainExpression: QTreeNode<CustomDTOExpression> = {
      operator: Operator.OpenBracket,
      relation: ExpressionRelationship.AND,
      children: roots,
      id: ListViewUtils.CRITERIA_EXP_ID++
    };

    if (!criteria || !criteria.expressions || !criteria.expressions.length)
      return [mainExpression];
    const current: QTreeNode<CustomDTOExpression>[] = [];
    const closeBracketRelations = new Map<CustomDTOExpression, string | undefined>();
    const relationFollowing = (node?: CustomDTOExpression) =>
      node?.operator === Operator.OpenBracket ? closeBracketRelations.get(node) : node?.relation;
    for (let expression of criteria.expressions) {
      let customExp = expression as CustomDTOExpression;
      customExp.id = ListViewUtils.CRITERIA_EXP_ID++;
      if (customExp.operator === Operator.OpenBracket) {
        if (!current.length) {
          roots.push(customExp);
        } else {
          let lastItem = current[current.length - 1];
          if (!lastItem.children)
            lastItem.children = [];
          lastItem.children.push(customExp);
        }
        current.push(customExp);
      } else if (customExp.operator === Operator.CloseBracket) {
        let openBracket = current.pop();
        if (!openBracket)
          openBracket = {id: ListViewUtils.CRITERIA_EXP_ID++};
        closeBracketRelations.set(openBracket, customExp.relation);
        openBracket.relation = openBracket.children?.length ? relationFollowing(openBracket.children[0]) : customExp.relation;
      } else {
        let mCriteriaField: CriteriaField | undefined = criteriaFields?.find(l => l.id === customExp.field);
        if (criteriaFields && criteriaFields.length && mCriteriaField) {
          if (customExp.operator === Operator.In || customExp.operator === Operator.NotIn)
            this.restoreInSelection(customExp, mCriteriaField);
          else {
            let parsedValue = ExpressionEditUtility.getParsedValue(mCriteriaField, customExp.rightHandSide, customExp.inValues);
            if (customExp.operator === Operator.WithinPeriod || customExp.operator === Operator.OutsidePeriod)
              parsedValue = customExp.rightHandSide;
            NamaCommon.setFieldValue(customExp, customExp.field!, parsedValue);
          }
        }
        if (!current.length) {
          roots.push(customExp);
        } else {
          let lastItem = current[current.length - 1];
          if (!lastItem.children)
            lastItem.children = [];
          lastItem.children.push(customExp);
        }
      }
    }
    mainExpression.relation = relationFollowing(roots[0]) ?? ExpressionRelationship.AND;
    this.trimSingleChildNodes(roots);
    if (roots.length === 1 && roots[0].operator === Operator.OpenBracket)
      return roots;
    return [mainExpression];
  }

  private static trimSingleChildNodes(expressions?: CustomDTOExpression[]) {
    if (!expressions)
      return;
    if (this.isOpenBracketOnly(expressions) && this.isOpenBracketOnly(expressions[0].children)) {
      expressions[0] = expressions[0].children![0];
    }
    for (let expression of expressions) {
      this.trimSingleChildNodes(expression?.children);
    }
  }

  private static isOpenBracketOnly(expressions?: CustomDTOExpression[]) {
    return expressions && expressions.length === 1 && expressions[0].operator === Operator.OpenBracket;
  }
  static removeChild(node: CustomDTOExpression, tree: QTreeNode<CustomDTOExpression>[]) {
    this.removeChildFromArr(node, tree);
  }

  static removeChildFromArr(node?: CustomDTOExpression, list?: CustomDTOExpression[]) {
    if (!list || !node)
      return;
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === node.id) {
        list.splice(i, 1);
        return;
      }
      this.removeChildFromArr(node, list[i].children as CustomDTOExpression[] | undefined);
    }
  }

  static appendExpressionNextToSource(source: CustomDTOExpression, isDuplicate: boolean, tree: QTreeNode<CustomDTOExpression>[]) {
    if (!source)
      return;
    let newNode: CustomDTOExpression = {};
    if (isDuplicate)
      newNode = NamaCommon.cloneObject(source);
    newNode.id = ListViewUtils.CRITERIA_EXP_ID++;
    this.appendNewExpressionInArr(source.id!, newNode, tree);
  }

  static appendNewExpressionInArr(sourceId: number, newNode: CustomDTOExpression, list?: CustomDTOExpression[]) {
    if (!sourceId || !list)
      return;
    for (let i = 0; i < list.length; i++) {
      if (list[i].id === sourceId) {
        list.splice(i + 1, 0, newNode);
        return;
      }
      this.appendNewExpressionInArr(sourceId, newNode, list[i].children as CustomDTOExpression[] | undefined);
    }
  }

  static updateCriteriaFromTree(criteria: DTOCriteria | undefined, criteriaTree: CustomDTOExpression[], criteriaFields: CriteriaField[] | undefined) {
    criteriaTree = this.updateRHSValuesInList(criteriaTree, criteriaFields);
    if (!criteriaTree || !criteriaTree.length)
      return criteria;
    if (!criteria)
      criteria = {};
    criteria.fromVue = true;
    criteria.expressions = criteriaTree;
    return criteria;
  }

  static updateRHSValuesInList(list: CustomDTOExpression[], criteriaFields: CriteriaField[] | undefined): CustomDTOExpression[] {
    if (!list)
      return [];
    const result: CustomDTOExpression[] = [];
    for (let i = 0; i < list.length; i++) {
      let exp = list[i];
      if (!exp.field && !exp.children)
        continue;
      if (exp.field && (exp.operator === Operator.WithinPeriod || exp.operator === Operator.OutsidePeriod))
        exp.rightHandSide = NamaCommon.getFieldValue(exp, exp.field);
      else if (exp.field && (exp.operator === Operator.In || exp.operator === Operator.NotIn))
        this.fillInValues(exp, criteriaFields);
      else if (exp.field)
        exp.rightHandSide = this.calcRightHandSide(exp, criteriaFields?.find(l => l.id === exp.field)?.metaData?.fieldType);
      if (exp.children)
        exp.children = this.updateRHSValuesInList(exp.children as CustomDTOExpression[], criteriaFields);
      result.push(NamaCommon.cloneObject(exp));
    }
    return result;
  }

  private static calcRightHandSide(exp: CustomDTOExpression, fieldType: string | undefined): string | undefined {
    const rightHandSide = FieldTypesUtils.toString(fieldType, NamaCommon.getFieldValue(exp, exp.field!));
    if (this.areSameDate(exp.rightHandSide, rightHandSide, fieldType))
      return exp.rightHandSide;
    return rightHandSide;
  }

  private static areSameDate(first: string | undefined, second: string | undefined, fieldType: string | undefined): boolean {
    if (!ObjectChecker.isAnyEqualToFirst(fieldType, FieldType.Date, FieldType.DateAndTime))
      return false;
    return FieldTypesUtils.parseString(first, fieldType) === FieldTypesUtils.parseString(second, fieldType);
  }

  private static restoreInSelection(exp: CustomDTOExpression, field: CriteriaField) {
    const fieldType = field.metaData?.fieldType;
    const rows = (exp.inValues ?? []).map((value, index) => ({
      id: `${exp.field}__ioo__${index}`,
      [exp.field!]: FieldTypesUtils.parseString(value, fieldType),
    }));
    NamaCommon.setFieldValue(exp, exp.field!, {[QuestionFieldsPresenter.detailId]: rows});
  }

  private static fillInValues(exp: CustomDTOExpression, criteriaFields: CriteriaField[] | undefined) {
    const fieldType = criteriaFields?.find(l => l.id === exp.field)?.metaData?.fieldType;
    const container = NamaCommon.getFieldValue(exp, exp.field!);
    const rows: any[] = container?.[QuestionFieldsPresenter.detailId] ?? [];
    exp.inValues = rows
      .map(row => row?.[exp.field!])
      .filter(value => ObjectChecker.isNotEmptyOrNull(value))
      .map(value => FieldTypesUtils.toString(fieldType, value));
    exp.rightHandSide = undefined;
  }
}
